import { Search, X } from 'lucide-react'
import {
  useEffect,
  useRef,
  useState,
  type KeyboardEvent as ReactKeyboardEvent,
} from 'react'

import { Button } from '../../../ui/Button/Button'
import type {
  ContactSearchResult,
  ContactSelection,
} from '../contact-directory-contracts'
import type { ContactDirectoryRepository } from '../contact-directory-repository'
import { executeStartConversation } from '../contact-directory-repository'
import styles from '../ConversationsPage.module.css'

type Status = 'idle' | 'loading' | 'ready' | 'error'

function unavailableReason(contact: ContactSearchResult) {
  if (contact.identityState !== 'EXACT_CONTACT_AUTHORITY')
    return 'Contact identity needs review'
  if (contact.destinationState === 'DESTINATION_UNAVAILABLE')
    return 'No confirmed SMS destination'
  if (contact.destinationState === 'DESTINATION_AMBIGUOUS')
    return 'SMS destination is ambiguous'
  if (contact.consentEvidenceState === 'OPTED_OUT') return 'Contact opted out'
  if (contact.consentEvidenceState === 'ELIGIBILITY_REVIEW_REQUIRED')
    return 'Messaging evidence needs review'
  return null
}

function evidenceLabel(contact: ContactSearchResult) {
  if (contact.consentEvidenceState === 'EXISTING_INBOUND_SMS_RELATIONSHIP')
    return 'Previous inbound SMS relationship'
  if (contact.consentEvidenceState === 'NO_POSITIVE_CONSENT_EVIDENCE')
    return 'No positive consent evidence recorded'
  return unavailableReason(contact)
}

function idempotencyKey() {
  const random = globalThis.crypto?.randomUUID?.()
  return random
    ? `contact-selection-${random}`
    : `contact-selection-${Date.now()}-fallback`
}

export function StartConversationDialog({
  tenantId,
  repository,
  onClose,
  onStarted,
  initialQuery = '',
}: {
  tenantId: string
  repository: ContactDirectoryRepository
  onClose: () => void
  onStarted?: (aggregateId: string) => void
  initialQuery?: string
}) {
  const dialog = useRef<HTMLDivElement>(null)
  const searchInput = useRef<HTMLInputElement>(null)
  const selectionKeys = useRef(new Map<string, string>())
  const startKey = useRef<string | null>(null)
  const [query, setQuery] = useState(initialQuery)
  const [submittedQuery, setSubmittedQuery] = useState('')
  const [status, setStatus] = useState<Status>('idle')
  const [results, setResults] = useState<ContactSearchResult[]>([])
  const [nextCursor, setNextCursor] = useState<string | null>(null)
  const [loadingMore, setLoadingMore] = useState(false)
  const [selectingRef, setSelectingRef] = useState<string | null>(null)
  const [selected, setSelected] = useState<{
    contact: ContactSearchResult
    proof: ContactSelection
  } | null>(null)
  const [draft, setDraft] = useState('')
  const [sendStatus, setSendStatus] = useState<
    'idle' | 'sending' | 'error' | 'reconciliation'
  >('idle')

  useEffect(() => {
    searchInput.current?.focus()
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault()
        onClose()
        return
      }
      if (event.key !== 'Tab' || !dialog.current) return
      const focusable = Array.from(
        dialog.current.querySelectorAll<HTMLElement>(
          'button:not([disabled]), input:not([disabled]), [tabindex]:not([tabindex="-1"])'
        )
      )
      const first = focusable[0]
      const last = focusable.at(-1)
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault()
        last?.focus()
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault()
        first?.focus()
      }
    }
    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [onClose])

  async function search() {
    const value = query.trim()
    if (value.length < 2) return
    setSubmittedQuery(value)
    setStatus('loading')
    setSelected(null)
    try {
      const response = await repository.search(tenantId, value)
      setResults(response.results)
      setNextCursor(response.nextCursor)
      setStatus('ready')
    } catch {
      setResults([])
      setNextCursor(null)
      setStatus('error')
    }
  }

  async function loadMore() {
    if (!nextCursor) return
    setLoadingMore(true)
    try {
      const response = await repository.search(
        tenantId,
        submittedQuery,
        nextCursor
      )
      setResults((current) => [
        ...current,
        ...response.results.filter(
          (result) =>
            !current.some((item) => item.contactRef === result.contactRef)
        ),
      ])
      setNextCursor(response.nextCursor)
    } catch {
      setStatus('error')
    } finally {
      setLoadingMore(false)
    }
  }

  async function select(contact: ContactSearchResult) {
    setSelectingRef(contact.contactRef)
    try {
      const key =
        selectionKeys.current.get(contact.contactRef) ?? idempotencyKey()
      selectionKeys.current.set(contact.contactRef, key)
      const proof = await repository.select(tenantId, contact.contactRef, key)
      setSelected({ contact, proof })
      startKey.current = null
    } catch {
      setStatus('error')
    } finally {
      setSelectingRef(null)
    }
  }

  async function send() {
    if (
      !selected ||
      selected.proof.evidence.consentEvidenceState !==
        'EXISTING_INBOUND_SMS_RELATIONSHIP'
    )
      return
    const content = draft.trim()
    if (
      !content ||
      content.length > 1600 ||
      sendStatus === 'sending' ||
      sendStatus === 'reconciliation'
    )
      return
    const key =
      startKey.current ??
      idempotencyKey().replace('contact-selection-', 'start-conversation-')
    startKey.current = key
    setSendStatus('sending')
    try {
      const result = await executeStartConversation(
        repository,
        tenantId,
        selected.proof.contactSelectionRef,
        key,
        content
      )
      if (result.status === 'ACCEPTED') {
        onStarted?.(result.aggregateId)
        return
      }
      setSendStatus(
        result.status === 'RECONCILIATION_REQUIRED' ? 'reconciliation' : 'error'
      )
    } catch {
      setSendStatus('error')
    }
  }

  function handleResultKeys(event: ReactKeyboardEvent<HTMLUListElement>) {
    if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp') return
    const buttons = Array.from(
      event.currentTarget.querySelectorAll<HTMLButtonElement>(
        'button:not([disabled])'
      )
    )
    if (!buttons.length) return
    const current = buttons.indexOf(document.activeElement as HTMLButtonElement)
    const next =
      event.key === 'ArrowDown'
        ? Math.min(current + 1, buttons.length - 1)
        : Math.max(current - 1, 0)
    event.preventDefault()
    buttons[next]?.focus()
  }

  return (
    <div className={styles.startConversationBackdrop}>
      <div
        ref={dialog}
        className={styles.startConversationDialog}
        role="dialog"
        aria-modal="true"
        aria-labelledby="start-conversation-title"
      >
        <header className={styles.startConversationHeader}>
          <div>
            <span className={styles.eyebrow}>Existing Flio contact</span>
            <h2 id="start-conversation-title">Start Conversation</h2>
          </div>
          <button
            type="button"
            aria-label="Close Start Conversation"
            onClick={onClose}
          >
            <X size={18} aria-hidden="true" />
          </button>
        </header>

        {selected ? (
          <section className={styles.contactReview} aria-live="polite">
            <span className={styles.contactReviewMark}>✓</span>
            <h3>{selected.contact.displayName}</h3>
            <p>Contact selected for future conversation setup.</p>
            <dl>
              <div>
                <dt>Destination</dt>
                <dd>{selected.contact.phoneDisplay ?? 'Unavailable'}</dd>
              </div>
              <div>
                <dt>Messaging evidence</dt>
                <dd>{evidenceLabel(selected.contact)}</dd>
              </div>
              <div>
                <dt>Conversation</dt>
                <dd>
                  {selected.proof.existingInteractionAggregateId
                    ? 'Existing conversation'
                    : 'No existing conversation'}
                </dd>
              </div>
            </dl>
            {selected.proof.evidence.consentEvidenceState ===
            'EXISTING_INBOUND_SMS_RELATIONSHIP' ? (
              <>
                <label htmlFor="start-conversation-message">
                  First staff message
                </label>
                <textarea
                  id="start-conversation-message"
                  value={draft}
                  maxLength={1600}
                  rows={4}
                  autoFocus
                  onChange={(event) => setDraft(event.target.value)}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter' && !event.shiftKey) {
                      event.preventDefault()
                      void send()
                    }
                  }}
                />
                <p className={styles.contactBoundaryNote}>
                  Sending pauses Flio AI for this conversation. If the staff
                  message is not accepted, Flio restores AI responses safely.
                </p>
                {sendStatus === 'error' ? (
                  <p role="alert">
                    The message was not confirmed. Try again with the same
                    draft.
                  </p>
                ) : null}
                {sendStatus === 'reconciliation' ? (
                  <p role="alert">
                    Flio couldn’t confirm whether this message was accepted.
                    Check the conversation before sending again.
                  </p>
                ) : null}
                <Button
                  type="button"
                  disabled={
                    !draft.trim() ||
                    sendStatus === 'sending' ||
                    sendStatus === 'reconciliation'
                  }
                  onClick={() => void send()}
                >
                  {sendStatus === 'sending'
                    ? 'Pausing AI and sending…'
                    : 'Pause AI and send'}
                </Button>
              </>
            ) : (
              <>
                <p className={styles.contactBoundaryNote}>
                  Selection does not authorize or send a message.
                </p>
                <p className={styles.contactBoundaryNote}>
                  Starting a message is unavailable because no positive inbound
                  SMS relationship is recorded.
                </p>
                <Button type="button" variant="secondary" onClick={onClose}>
                  Done
                </Button>
              </>
            )}
          </section>
        ) : (
          <>
            <form
              className={styles.contactSearchForm}
              onSubmit={(event) => {
                event.preventDefault()
                void search()
              }}
            >
              <label htmlFor="contact-directory-search">
                Search by name, phone, or email prefix
              </label>
              <div>
                <Search size={17} aria-hidden="true" />
                <input
                  ref={searchInput}
                  id="contact-directory-search"
                  type="search"
                  value={query}
                  minLength={2}
                  maxLength={80}
                  placeholder="Search existing contacts"
                  onChange={(event) => setQuery(event.target.value)}
                />
                <Button
                  type="submit"
                  disabled={query.trim().length < 2 || status === 'loading'}
                >
                  Search
                </Button>
              </div>
            </form>
            <div className={styles.contactSearchStatus} aria-live="polite">
              {status === 'idle'
                ? 'Search uses exact prefixes for candidate discovery.'
                : null}
              {status === 'loading' ? 'Searching contacts…' : null}
              {status === 'error'
                ? 'Contact search could not be completed. Try again.'
                : null}
              {status === 'ready' && results.length === 0
                ? 'No matching contacts found.'
                : null}
            </div>
            {results.length ? (
              <ul
                className={styles.contactResults}
                onKeyDown={handleResultKeys}
              >
                {results.map((contact) => {
                  const reason = unavailableReason(contact)
                  return (
                    <li key={contact.contactRef}>
                      <button
                        type="button"
                        disabled={
                          !contact.selectionAvailable || selectingRef !== null
                        }
                        aria-describedby={`contact-evidence-${contact.contactRef}`}
                        onClick={() => void select(contact)}
                      >
                        <span className={styles.contactIdentity}>
                          <strong>{contact.displayName}</strong>
                          <span>
                            {[contact.phoneDisplay, contact.emailDisplay]
                              .filter(Boolean)
                              .join(' · ') || 'No contact details available'}
                          </span>
                        </span>
                        <span
                          className={styles.contactEvidence}
                          id={`contact-evidence-${contact.contactRef}`}
                        >
                          {contact.existingInteractionAggregateId
                            ? 'Existing conversation · '
                            : ''}
                          {reason ?? evidenceLabel(contact)}
                        </span>
                      </button>
                    </li>
                  )
                })}
              </ul>
            ) : null}
            {nextCursor ? (
              <Button
                type="button"
                variant="secondary"
                disabled={loadingMore}
                onClick={() => void loadMore()}
              >
                {loadingMore ? 'Loading…' : 'Load more'}
              </Button>
            ) : null}
            <p className={styles.contactBoundaryNote}>
              Search finds candidates only. No conversation or message is
              created here.
            </p>
          </>
        )}
      </div>
    </div>
  )
}
